import { ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  BiChevronsRight,
  BiChevronsLeft,
  BiChevronDown,
  BiChevronUp,
} from 'react-icons/bi';
import { BsFillHouseFill } from 'react-icons/bs';
import { SCRIPTS_PATH } from '~/services/endpoints';
import { LABELS } from '~/resources/Labels';

interface MenuProps {
  children?: ReactNode;
}

export const SIMPLMenuHeader = ({ children }: MenuProps) => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isVmSubMenuOpen, setIsVmSubMenuOpen] = useState(false);

  const getNavLinkClass = (isActive: boolean) => {
    const baseStyles =
      'text-sm font-semibold text-white flex items-center px-4 py-2 rounded-md transition-all border w-full';
    const activeStyles =
      'bg-opacity-10 bg-[#51E7F9] border-[#51E7F9] border-[1px] text-[#51E7F9]';
    const inactiveStyles = 'border-transparent text-white';
    const hoverStyles =
      'hover:border-[#51E7F9] hover:bg-opacity-10 hover:bg-[#51E7F9] hover:border-[1px] hover:text-[#51E7F9]';

    return `${baseStyles} ${isActive ? activeStyles : inactiveStyles} ${hoverStyles}`;
  };

  const vmTemplateSubItems = [
    { to: '/vm-template/configuration', label: 'VM Configuration' },
    { to: '/vm-template/management', label: 'Management' },
    { to: '/vm-template/post-provisioning', label: 'Post Provisioning' },
    { to: '/vm-template/policies', label: 'Policies' },
  ];

  return (
    <div className="flex flex-col h-screen relative">
      {/* Top Menu */}
      <div className="flex justify-start items-center bg-[#002B6E] text-white px-2 sm:px-4 md:px-6 py-3 relative">
        <div className="flex items-center justify-start w-full">
          <span className="text-white text-2xl font-bold tracking-widest uppercase">Infrastructure</span>
        </div>
      </div>

      {/* Main Content Wrapper */}
      <div className="flex flex-1 relative flex-col md:flex-row">
        {/* Sidebar */}
        <div
          className={`bg-[#002B6E] ${
            isSidebarExpanded
              ? 'w-full md:w-[240px]'
              : 'w-full md:w-[60px] md:rounded-tr-[20px] md:rounded-br-[20px]'
          } 
          p-3 md:p-5 text-white transition-all duration-300 flex flex-col md:ml-auto overflow-hidden relative`}
        >
          <span className="h-[15px]"></span>

          {/* Sidebar Toggle and Menu Text */}
          <div className="flex items-center justify-between mb-8">
            <span
              className={`${
                isSidebarExpanded ? 'inline-flex items-center' : 'hidden md:inline-flex'
              } text-sm font-semibold`}
            >
              <BsFillHouseFill size={22} className="mr-1" />
              Menu
            </span>

            <button
              onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
              className="ml-auto w-[22px] h-[20px] gap-8 rounded-md border-[1px] border-[#51E7F9] 
              p-4 flex items-center justify-center transition-all duration-300"
            >
              {isSidebarExpanded ? (
                <span className="text-sm font-semibold text-[#51E7F9]">
                  <BiChevronsLeft size={14} />
                </span>
              ) : (
                <span className="text-sm font-semibold text-[#51E7F9]">
                  <BiChevronsRight size={14} />
                </span>
              )}
            </button>
          </div>

          <nav className="flex-1">
            <ul className="space-y-3">
              {/* Deployment Script */}
              <li className="mb-2 flex items-center">
                <NavLink
                  to={SCRIPTS_PATH}
                  className={({ isActive }) =>
                    isSidebarExpanded ? getNavLinkClass(isActive) : ''
                  }
                >
                  <span
                    className={`text-[14px] leading-[16px] align-middle flex-1 ${
                      isSidebarExpanded ? 'inline' : 'hidden'
                    }`}
                  >
                    {LABELS.DeploymentScript}
                  </span>
                </NavLink>
              </li>

              {/* Contract */}
              <li className="mb-2 flex items-center">
                <NavLink
                  to="/contracts"
                  className={({ isActive }) =>
                    isSidebarExpanded ? getNavLinkClass(isActive) : ''
                  }
                >
                  <span
                    className={`text-[14px] leading-[16px] align-middle flex-1 ${
                      isSidebarExpanded ? 'inline' : 'hidden'
                    }`}
                  >
                    {LABELS.Contract}
                  </span>
                </NavLink>
              </li>

              {/* Cloud Environment */}
              <li className="mb-2 flex items-center">
                <NavLink
                  to="/cloud-environment"
                  className={({ isActive }) =>
                    isSidebarExpanded ? getNavLinkClass(isActive) : ''
                  }
                >
                  <span
                    className={`text-[14px] leading-[16px] align-middle flex-1 ${
                      isSidebarExpanded ? 'inline' : 'hidden'
                    }`}
                  >
                    {LABELS.CloudEnviroment}
                  </span>
                </NavLink>
              </li>

              {/* Cloud Provisioner Template */}
              <li className="mb-2 flex items-center">
                <NavLink
                  to="/cloud-provisioner-template"
                  className={({ isActive }) =>
                    isSidebarExpanded ? getNavLinkClass(isActive) : ''
                  }
                >
                  <span
                    className={`text-[14px] leading-[16px] align-middle flex-1 ${
                      isSidebarExpanded ? 'inline' : 'hidden'
                    }`}
                  >
                    {LABELS.CloudProvisionerTemplateManagement}
                  </span>
                </NavLink>
              </li>

              {/* VM Template + Submenu */}
              <li className="mb-2">
                <div
                  className={`flex items-center justify-between px-4 py-2 rounded-md transition-all border 
                  ${isSidebarExpanded ? 'w-full' : 'hidden'} 
                  border-transparent text-white hover:border-[#51E7F9] hover:bg-opacity-10 hover:bg-[#51E7F9] hover:border-[1px] hover:text-[#51E7F9]`}
                >
                  <nav
                    className="text-sm font-semibold flex items-center flex-1"
                  >
                    <span className="text-[14px] leading-[16px] flex-1">
                      {LABELS.VMTemplate}
                    </span>
                  </nav>
                  <button
                    onClick={() => setIsVmSubMenuOpen(!isVmSubMenuOpen)}
                    className="ml-2"
                  >
                    {isVmSubMenuOpen ? (
                      <BiChevronUp size={18} className="text-[#51E7F9]" />
                    ) : (
                      <BiChevronDown size={18} className="text-[#51E7F9]" />
                    )}
                  </button>
                </div>

                {isVmSubMenuOpen && isSidebarExpanded && (
                  <ul className="ml-6 mt-2 space-y-2">
                    {vmTemplateSubItems.map(({ to, label }) => (
                      <li key={to}>
                        <NavLink
                          to={to}
                          className={({ isActive }) =>
                            getNavLinkClass(isActive)
                          }
                        >
                          <span className="text-[14px] leading-[16px] flex-1">
                            {label}
                          </span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            </ul>
          </nav>
        </div>

        {/* Page Content */}
        <div className="p-4 md:p-6 flex-1 overflow-auto">
          {Array.isArray(children)
            ? children.map((child, index) => <div key={index}>{child}</div>)
            : children}
        </div>
      </div>
    </div>
  );
};